import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge, Card, Empty, ErrorBox, ReviewBanner, Skeleton } from "@/components/ui";
import type {
  KnowledgeDocument,
  KnowledgeScanResult,
  KnowledgeSearchHit,
  RecommendationDraft,
} from "@/types";

const SITES = [
  "", "breast", "prostate", "lung", "rectal_gi", "head_neck",
  "cns", "gyne", "sarcoma", "palliative_metastatic", "other", "unknown",
];
const ORGS = ["", "NCCN", "ESMO", "ESTRO", "ASTRO", "DEGRO", "BOOK", "PAPER", "LOCAL_PROTOCOL", "USER_SUMMARY", "UNKNOWN"];

export default function KnowledgeLibrary() {
  const qc = useQueryClient();
  const [source, setSource] = useState("");
  const [site, setSite] = useState("");
  const [status, setStatus] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [question, setQuestion] = useState("");
  const [questionSite, setQuestionSite] = useState("");
  const [lastRec, setLastRec] = useState<RecommendationDraft | null>(null);

  const docs = useQuery({
    queryKey: ["knowledge-docs", source, site, status],
    queryFn: () => {
      const params = new URLSearchParams();
      if (source) params.set("source", source);
      if (site) params.set("disease_site", site);
      if (status) params.set("status", status);
      return api.get<KnowledgeDocument[]>(`/knowledge/documents?${params.toString()}`);
    },
  });

  const scan = useMutation({
    mutationFn: () => api.post<KnowledgeScanResult>("/knowledge/scan"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["knowledge-docs"] }),
  });

  const search = useQuery({
    queryKey: ["knowledge-search", searchQ, site, source],
    enabled: searchQ.trim().length > 1,
    queryFn: () => {
      const params = new URLSearchParams({ q: searchQ });
      if (site) params.set("disease_site", site);
      if (source) params.set("source", source);
      return api.get<KnowledgeSearchHit[]>(`/knowledge/search?${params.toString()}`);
    },
  });

  const recommend = useMutation({
    mutationFn: () =>
      api.post<RecommendationDraft>("/knowledge/recommendation-drafts", {
        clinical_question: question,
        disease_site: questionSite || undefined,
      }),
    onSuccess: (data) => setLastRec(data),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-50">Knowledge Library</h1>
        <div className="text-sm text-slate-400">
          Drop guidelines/papers/books into the knowledge dropbox folder, then
          press scan. Each recommendation cites its sources verbatim.
        </div>
      </div>

      <ReviewBanner />

      <Card
        title="Library actions"
        actions={
          <button
            className="btn-primary"
            disabled={scan.isPending}
            onClick={() => scan.mutate()}
          >
            {scan.isPending ? "Scanning…" : "Scan dropbox"}
          </button>
        }
      >
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="label">Source organization</label>
            <select className="input" value={source} onChange={(e) => setSource(e.target.value)}>
              {ORGS.map((s) => (
                <option key={s} value={s}>{s || "Any"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Disease site</label>
            <select className="input" value={site} onChange={(e) => setSite(e.target.value)}>
              {SITES.map((s) => (
                <option key={s} value={s}>{s || "Any"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Classification status</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any</option>
              <option value="auto_classified">Auto-classified</option>
              <option value="needs_review">Needs review</option>
              <option value="reviewed">Reviewed</option>
            </select>
          </div>
        </div>
        {scan.data && (
          <div className="mt-3 text-xs text-slate-300">
            Scan: scanned <b>{scan.data.scanned}</b>, new <b>{scan.data.new_documents}</b>,
            skipped duplicates <b>{scan.data.skipped_duplicates}</b>,
            needs review <b>{scan.data.needs_review}</b>.
          </div>
        )}
        {scan.error ? <ErrorBox error={scan.error} /> : null}
      </Card>

      <Card title="Documents">
        {docs.isLoading ? (
          <Skeleton rows={4} />
        ) : (docs.data || []).length === 0 ? (
          <Empty title="No documents yet." hint="Drop files into the knowledge dropbox folder and click Scan." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-400">
                <tr>
                  <th className="text-left p-2">File</th>
                  <th className="text-left p-2">Source</th>
                  <th className="text-left p-2">Site</th>
                  <th className="text-left p-2">Kind</th>
                  <th className="text-left p-2">Year</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(docs.data || []).map((d) => (
                  <tr key={d.id} className="border-t border-line/50">
                    <td className="p-2 text-slate-100">
                      <div>{d.detected_title || d.original_filename}</div>
                      <div className="text-xs text-slate-500">{d.original_filename}</div>
                    </td>
                    <td className="p-2"><Badge tone="info">{d.detected_source_organization}</Badge></td>
                    <td className="p-2">{d.detected_disease_site}</td>
                    <td className="p-2">{d.document_kind}</td>
                    <td className="p-2">{d.detected_year ?? "—"}</td>
                    <td className="p-2">
                      <Badge tone={
                        d.classification_status === "reviewed" ? "ok"
                          : d.classification_status === "needs_review" ? "warn" : "info"
                      }>
                        {d.classification_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Search the library">
        <div className="flex gap-2 items-end mb-3">
          <div className="flex-1">
            <label className="label">Query</label>
            <input
              className="input"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="e.g. adjuvant radiotherapy node positive"
            />
          </div>
        </div>
        {search.isLoading ? (
          <Skeleton rows={3} />
        ) : (search.data || []).length === 0 && searchQ.trim().length > 1 ? (
          <Empty title="No matching chunks." />
        ) : (
          <ul className="space-y-2">
            {(search.data || []).map((h) => (
              <li key={h.chunk.id} className="card p-3">
                <div className="text-xs text-slate-400 mb-1">
                  <Badge tone="info">{h.document.detected_source_organization}</Badge>{" "}
                  {h.document.detected_title || h.document.original_filename}
                  {" · "} site {h.document.detected_disease_site}
                  {h.chunk.page_number ? ` · p.${h.chunk.page_number}` : ""}
                </div>
                <div className="text-sm text-slate-200 whitespace-pre-wrap">{h.chunk.text.slice(0, 500)}</div>
                <div className="text-xs text-slate-500 mt-1">score: {h.score.toFixed(2)}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="Generate evidence-based recommendation draft"
        actions={
          <button
            className="btn-primary"
            disabled={recommend.isPending || question.trim().length < 5}
            onClick={() => recommend.mutate()}
          >
            {recommend.isPending ? "Drafting…" : "Generate draft"}
          </button>
        }
      >
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="label">Disease site (optional)</label>
            <select className="input" value={questionSite} onChange={(e) => setQuestionSite(e.target.value)}>
              {SITES.map((s) => (
                <option key={s} value={s}>{s || "Any"}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Clinical question</label>
            <input
              className="input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Adjuvant RT after BCS in node-positive HR+ breast cancer?"
            />
          </div>
        </div>
        {recommend.error ? <ErrorBox error={recommend.error} /> : null}

        {lastRec && (
          <div className="mt-4 space-y-3">
            <div className="text-sm text-slate-300">
              <Badge tone="info">{lastRec.disease_site || "site unknown"}</Badge>{" "}
              confidence {Math.round((lastRec.confidence || 0) * 100)}%
            </div>
            <div className="card p-3 bg-bg-alt/40">
              <div className="text-xs uppercase text-slate-400 mb-1">Sources reviewed</div>
              {lastRec.sources.length === 0 ? (
                <div className="text-sm text-slate-400">No matching sources in the library.</div>
              ) : (
                <ul className="space-y-2">
                  {lastRec.sources.map((s) => (
                    <li key={s.id}>
                      <div className="text-xs text-slate-400">
                        <Badge tone="info">{s.source_organization || "?"}</Badge>{" "}
                        {s.source_title}
                        {s.page_number ? ` · p.${s.page_number}` : ""}
                        {" · "}
                        <Badge tone={s.relevance_strength === "strong" ? "ok" : s.relevance_strength === "partial" ? "warn" : "muted"}>
                          {s.relevance_strength}
                        </Badge>{" "}
                        <Badge tone="muted">{s.evidence_status}</Badge>
                      </div>
                      <div className="text-sm text-slate-200 whitespace-pre-wrap mt-1">
                        {s.source_says}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="card p-3 bg-bg-alt/40">
              <div className="text-xs uppercase text-slate-400 mb-1">Synthesis</div>
              <div className="text-sm text-slate-100">{lastRec.synthesis_text}</div>
            </div>
            <div className="card p-3 bg-bg-alt/40 border-l-4 border-amber-500/70">
              <div className="text-xs uppercase text-amber-300 mb-1">RPIS Learned Interpretation</div>
              <div className="text-sm text-slate-100">{lastRec.learned_interpretation_text}</div>
            </div>
            {lastRec.missing_data.length > 0 && (
              <div className="card p-3 bg-bg-alt/40">
                <div className="text-xs uppercase text-slate-400 mb-1">Missing data</div>
                <ul className="list-disc pl-5 text-sm text-slate-200">
                  {lastRec.missing_data.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
